import { useContext, useMemo, useState } from 'react';
import Image from '@next/image';

import withMetatags from 'core/hocs/withMetatags';
import { QueryFilter } from 'types/Filter';
import { TrustedUs } from 'types/models/TrustedUs';
import { Testimonial } from 'types/models/Testimonial';
import { searchWithFiltersRoute } from 'apiRoutes/search';
import { SortId, sortValues } from 'consts/sort';
import type { SearchWithFiltersRoute } from 'api/search/with-filters';
import type { GetArticleCategoriesWithArticlesResult } from 'graphql/queries/GetArticlesCategoriesWithArticles.query';
import { FilesContext } from '@providers/FilesProvider';
import VariantsList from '@components/VariantsList';
import ExpertSection from '@pages/PublicSector/components/ExpertSection';
import ContentBox from '@components/atoms/ContentBox';
import { TrustedUsView } from '@components/TrustedUs';
import ClientsAboutUs from '@components/ClientsAboutUs';
import Section from '@components/Section';
import Typography from '@components/atoms/Typography';
import SearchEngineText from '@components/SearchEngineText';
import ArticleCategoryPreview from '@components/ArticleCategoryPreview';
import Tags from '@components/Tags';
import { FormattedMessage } from 'utils/intl';
import { GetResponse } from 'utils/apiRoute';
import { getSearchConstantFilters } from 'utils/product';
import { ResolveConfig } from 'utils/resolveImageUrl';
import map from 'utils/map';
import useFetch from 'hooks/useFetch';

import SchoolTrustedUs from './SchoolTrustedUs';
import EducationHeroSection from './EducationHeroSection';
import EducationContactUs from './EducationContactUs';
import EducationZoneArticle from './EducationZoneArticle';
import { sections, expertItems, hightSchools, searchEngineTexts, primarySchools } from './EducationZone.consts';
import classes from './EducationZone.module.scss';
import messages from './EducationZone.messages';

type Props = {
  searchData?: GetResponse<SearchWithFiltersRoute>;
  articleCategories?: GetArticleCategoriesWithArticlesResult['articleCategories'];
  trustedUsLogos?: Pick<TrustedUs, 'id' | 'image' | 'url' | 'name'>[];
  testimonials?: Testimonial[];
};

const EducationZone = ({
  searchData: initialSearchData,
  articleCategories = [],
  trustedUsLogos = [],
  testimonials = [],
}: Props): JSX.Element => {
  const { getPageImageConfigs } = useContext(FilesContext);
  const [activeId, setActiveId] = useState(0);
  const partners = getPageImageConfigs(['dell', 'microsoft', 'tp-link', 'dicota']);
  const [refetch, { data: searchWithFiltersData, timestamp }] = useFetch(searchWithFiltersRoute, {
    initialData: initialSearchData,
  });

  const searchData = useMemo(
    () => ({
      ...searchWithFiltersData,
      constantFilters: [
        ...sections[activeId].constantFilters,
        ...getSearchConstantFilters(searchWithFiltersData?.hits || 0, searchWithFiltersData?.filters || []),
      ],
    }),
    [searchWithFiltersData] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onClick = (index: number, constantFilters: QueryFilter[]) => {
    setActiveId(index);
    refetch({
      name: '',
      mode: 'category',
      limit: 20,
      offset: 0,
      order: sortValues[SortId.Popularity],
      filters: constantFilters,
    });
  };

  return (
    <div className={classes.wrapper}>
      <EducationHeroSection
        title={<FormattedMessage {...messages.title} />}
        subtitle={<FormattedMessage {...messages.subtitle} />}
      />
      <div className={classes.container}>
        <section className={classes.navigationSection}>
          <div className={classes.navigationSectionContent}>
            <Typography variant="h1" renderAs="div" className={classes.navigationHeader}>
              <FormattedMessage {...messages.firstSectionHeader} />
            </Typography>
            <Typography variant="body2" className={classes.navigationDescription}>
              <FormattedMessage {...messages.firstSectionDescription} />
            </Typography>
          </div>
          <Tags
            size="lg"
            items={sections.map(({ name, constantFilters }, i) => ({
              id: i,
              name,
              onClick: () => onClick(i, constantFilters),
            }))}
            activeId={activeId}
          />
        </section>
        {searchData && (
          <Section title={<FormattedMessage {...messages.variantsSections} />}>
            <VariantsList
              initializeKey={timestamp}
              displayMode="tile"
              variants={searchData.search || []}
              constantFilters={searchData.constantFilters}
              filters={searchData.filters || []}
              hits={searchData.hits || 0}
              sortValues={sortValues}
              showAllFilters
              showAll
            />
          </Section>
        )}
        <EducationContactUs />
        <div className={classes.partners}>
          <ClientsAboutUs variant="box" className={classes.column} testimonials={testimonials} />
          <ContentBox variant="border" className={classes.column}>
            <div className={classes.partnersBox}>
              <div>
                <Typography variant="h1" renderAs="div" className={classes.partnersHeader}>
                  <FormattedMessage {...messages.partners} />
                </Typography>
                <Typography variant="body1" className={classes.partnersDescription} renderAs="div">
                  <FormattedMessage {...messages.partnersDescription} />
                </Typography>
              </div>
              <div className={classes.partnersLogos}>
                {partners
                  .filter((image) => !!image)
                  .map((image) => (
                    <Image
                      key={image?.name}
                      alt={`${image?.name}`}
                      className={classes.partnerLogo}
                      resolveConfig={image as ResolveConfig}
                    />
                  ))}
              </div>
            </div>
          </ContentBox>
        </div>
        <div className={classes.mainArticle}>
          <EducationZoneArticle />
        </div>
        {!!articleCategories.length &&
          articleCategories.map((articleCategory) => (
            <ArticleCategoryPreview
              key={articleCategory.id}
              name={articleCategory.name}
              slug={articleCategory.slug}
              articles={map(articleCategory.articles, 'article')}
              withFakeLink
              withSpacing
            />
          ))}
        <Section title={<FormattedMessage {...messages.cooperativeSection} />}>
          <ExpertSection
            title={<FormattedMessage {...messages.expertTitle} />}
            description={<FormattedMessage {...messages.expertDescription} />}
            itemsPerRow={2}
            items={expertItems}
          />
        </Section>
        <Section title={<FormattedMessage {...messages.universitiesSection} />}>
          <TrustedUsView logos={trustedUsLogos} limit={12} />
        </Section>
        <Section title={<FormattedMessage {...messages.highSchoolsSection} />}>
          <SchoolTrustedUs type="high" schools={hightSchools} />
        </Section>
        <Section title={<FormattedMessage {...messages.primarySchoolsSection} />}>
          <SchoolTrustedUs type="primary" schools={primarySchools} />
        </Section>
        <div className={classes.lastSection}>
          <SearchEngineText texts={searchEngineTexts} />
        </div>
      </div>
    </div>
  );
};

export default withMetatags(EducationZone, { withCanonicalUrl: true });
